import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  BBBVideo,
  CBTVideo,
  firstVerseNumber,
  getFirstChapterVerse,
} from '@deaf-bible-society-2-0/deafbible-metadata-utils';

import { RootState } from '../store';
import { lookupTable } from '../store/translation/selectors';
import { getURLParams } from '../utils/history';
import { setLastURL, setPrevTextVersion } from '../utils/localStorage';
import { introUrlVerseForVideo, resolveChapterIntroVerse } from '../utils/introResolver';
import getTimestamps from '../utils/api/getTimestamps';
import { getBBBVideoId } from '../utils/videoHelper';
import { hasIntroSegment, isIntroVerse } from '../utils/timestamp';

interface URLManipulator {
  selectBBBVideo: (video: BBBVideo) => void;
  selectBBBVideoReset: (video: BBBVideo) => void;
  selectCBTVideo: (video: CBTVideo) => void;
  toggleTextVersion: (textVersion: string | undefined) => void;
  updateChapterVerse: (
    newChapter: string,
    newVerse: string,
    textVersion?: string,
    bookCode?: string
  ) => void;
  updateChapterVerseForSearchResult: (
    newChapter: string,
    newVerse: string,
    textVersion?: string,
    bookCode?: string
  ) => void;
}

const useURLManipulator = (): URLManipulator => {
  const history = useHistory();

  const books = useSelector((state: RootState) => state.translation.rawData?.books);
  const lut = useSelector(lookupTable);

  const stories = useSelector((state: RootState) => state.translation.rawData?.stories);

  const { code, bookOrSlug, chapter, verse } = getURLParams();

  const resolvePreferredVerse = useCallback(
    (targetBook: string, targetChapter: string, fallbackVerse: string): Promise<string> =>
      resolveChapterIntroVerse(lut, code, targetBook, targetChapter, fallbackVerse),
    [code, lut]
  );

  const updateChapterVerse = useCallback(
    (newChapter: string, newVerse: string, textVersion?: string) => {
      if (verse !== newVerse || chapter !== newChapter) {
        const url = new URL(window.location.toString());
        const currentTextVersion = url.searchParams.get('text');

        // Keep the current text version if no new one is provided
        const finalTextVersion = textVersion || currentTextVersion;

        history.replace(
          `/${code}/${bookOrSlug}.${newChapter}.${newVerse}${
            finalTextVersion ? `?text=${finalTextVersion}` : ''
          }`
        );
      }
    },
    [bookOrSlug, chapter, code, history, verse]
  );

  const updateChapterVerseForSearchResult = useCallback(
    (newChapter: string, newVerse: string, textVersion?: string, bookCode?: string) => {
      if (verse !== newVerse || chapter !== newChapter) {
        const url = new URL(window.location.toString());
        const currentTextVersion = url.searchParams.get('text');

        // Keep the current text version if no new one is provided
        const finalTextVersion = textVersion || currentTextVersion;

        history.replace(
          `/${code}/${bookCode}.${newChapter}.${newVerse}${
            finalTextVersion ? `?text=${finalTextVersion}` : ''
          }`
        );
      }
    },
    [code, chapter, history, verse]
  );

  const toggleTextVersion = useCallback(
    (textVersion: string | undefined): void => {
      const url = new URL(window.location.toString());
      const { searchParams: params } = url;

      if (textVersion === undefined) {
        const currentVersion = params.get('text');
        if (currentVersion) {
          setPrevTextVersion(currentVersion);
        }
        params.delete('text');
      } else {
        params.set('text', textVersion);
        setPrevTextVersion(textVersion);
      }
      const newUrl = `${url.pathname}${url.search}`;

      setLastURL(newUrl);
      history.push(newUrl);
    },
    [history]
  );

  const selectBBBVideo = useCallback(
    (video: BBBVideo): void => {
      if (bookOrSlug === undefined || chapter === undefined) {
        return;
      }

      const newVerse = firstVerseNumber(video, chapter);

      if (newVerse === undefined) {
        return;
      }

      const navigate = async () => {
        const chapterVideos = lut?.videosInChapter(bookOrSlug, chapter) ?? [video];
        const chapterNumber = parseInt(chapter, 10);
        const introVerse = introUrlVerseForVideo(video, chapter, chapterVideos);

        // Already on this video's intro — move to the first numbered verse so the
        // range button can leave intro instead of re-selecting it.
        if (isIntroVerse(verse) && verse === introVerse) {
          history.push(`/${code}/${bookOrSlug}.${chapter}.${newVerse}${window.location.search}`);
          return;
        }

        try {
          const records = await getTimestamps(
            code,
            bookOrSlug,
            getBBBVideoId(video),
            video.bbb_id,
            video.chapter
          );

          if (
            !Number.isNaN(chapterNumber) &&
            hasIntroSegment(records, chapterNumber, video.bbb_id)
          ) {
            history.push(
              `/${code}/${bookOrSlug}.${chapter}.${introVerse}${window.location.search}`
            );
            return;
          }
        } catch {
          // Fall through to the first numbered verse when timestamps are unavailable.
        }

        history.push(`/${code}/${bookOrSlug}.${chapter}.${newVerse}${window.location.search}`);
      };

      navigate().catch(() => undefined);
    },
    [bookOrSlug, chapter, code, history, lut, verse]
  );

  const selectBBBVideoReset = useCallback(
    (video: BBBVideo): void => {
      // Resolve the book by the video's id rather than its (English) title, so
      // auto-advance works for translations whose book titles aren't English
      // (e.g. Korean) and across missing-chapter gaps.
      const newBook = books?.find(book =>
        book.videos?.some(bookVideo => bookVideo.bbb_id === video.bbb_id)
      )?.book_shortcode;

      if (newBook !== undefined) {
        const [newChapter, newVerse] = getFirstChapterVerse(video);
        const updateUrl = async () => {
          const fallbackVerse = `${newVerse}`;
          const chapterVideos = lut?.videosInChapter(newBook, newChapter) ?? [video];
          const chapterNumber = parseInt(newChapter, 10);

          try {
            const records = await getTimestamps(
              code,
              newBook,
              getBBBVideoId(video),
              video.bbb_id,
              video.chapter
            );

            // Prefer this video's own intro (e.g. Mark 15:21-47 → 21.0) over the
            // chapter-level intro check, which only looks at the chapter opener.
            if (
              !Number.isNaN(chapterNumber) &&
              hasIntroSegment(records, chapterNumber, video.bbb_id)
            ) {
              const introVerse = introUrlVerseForVideo(video, newChapter, chapterVideos);
              history.push(
                `/${code}/${newBook}.${newChapter}.${introVerse}${window.location.search}`
              );
              return;
            }
          } catch {
            // Fall through to chapter-level intro resolution.
          }

          const preferredVerse = await resolvePreferredVerse(newBook, newChapter, fallbackVerse);
          history.push(
            `/${code}/${newBook}.${newChapter}.${preferredVerse}${window.location.search}`
          );
        };

        updateUrl().catch(() => undefined);
      }
    },
    [books, history, code, lut, resolvePreferredVerse]
  );

  const selectCBTVideo = useCallback(
    (video: CBTVideo): void => {
      const meta = stories?.find(story => story.videos.some(v => v === video));

      if (meta !== undefined) {
        history.push(`/${code}/${meta.slug}/${video.type}${window.location.search}`);
      }
    },
    [code, history, stories]
  );

  return {
    updateChapterVerse,
    selectBBBVideo,
    selectBBBVideoReset,
    selectCBTVideo,
    toggleTextVersion,
    updateChapterVerseForSearchResult,
  };
};

export default useURLManipulator;
